import { DestroyRef, EventEmitter, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { CUSTOM_PDF_REPORT_MODAL_STATE_SLICE_KEY } from 'CustomPDFReportsApp/ts/features/custom-pdf-report-modal/state/reducer';
import { getSelectedReport } from 'CustomPDFReportsApp/ts/features/custom-pdf-report-modal/state/selectors';
import { BULLET_POINT_TOOLBAR_FORMATS, QuillToolbarFormatType } from '../text-edit/constants';
import { CustomPDFReportModulePreviewImageService } from 'CustomPDFReportsApp/ts/services/custom-pdf-report-module-preview-image.service';
import { ConfirmModalComponent } from 'Angular/features/components/modals/confirm-modal/confirm-modal.component';
import { TextEditComponent } from 'Angular/features/components/modals/text-edit/text-edit.component';
import * as i0 from "@angular/core";
import * as i1 from "CustomPDFReportsApp/ts/services/custom-pdf-report-module-preview-image.service";
import * as i2 from "@ngrx/store";
import * as i3 from "../confirm-modal/confirm-modal.component";
import * as i4 from "../modal-base/components/modal-base.component";
import * as i5 from "../modal-base/components/simple-modal-footer.component";
import * as i6 from "../modal-base/components/simple-modal-header.component";
import * as i7 from "Angular/features/components/modals/text-edit/text-edit.component";
const _c0 = ["textEditComponent"];
function TalkingPointTextEditModalComponent_Conditional_5_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 6);
    i0.ɵɵelement(1, "div", 11);
    i0.ɵɵelementEnd();
} }
function TalkingPointTextEditModalComponent_Conditional_6_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "img", 7);
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("src", ctx_r0.image, i0.ɵɵsanitizeUrl);
} }
function TalkingPointTextEditModalComponent_Conditional_7_Template(rf, ctx) { if (rf & 1) {
    const _r2 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "ycn-text-edit", 12, 0);
    i0.ɵɵlistener("textChanged", function TalkingPointTextEditModalComponent_Conditional_7_Template_ycn_text_edit_textChanged_0_listener($event) { i0.ɵɵrestoreView(_r2); const ctx_r0 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r0.newText = $event); });
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("extraFormats", ctx_r0.quillFormats)("text", ctx_r0.text);
} }
/**
 *
 * Talking Point Text Edit Modal Component
 *
 * Allows text edition with a WYSIWYG editor for talking points. Text is saved as HTML.
 *
 *
 * Input Properties:
 *
 *  talkingPoint: Talking point to be edited
 *  defaultText: Default text to set text editor if reset
 *
 *
 * Output Properties:
 *
 *  textSaved: EventEmitter that emits when saving the edited text (as HTML string).
 *
 */
export class TalkingPointTextEditModalComponent {
    constructor(previewImageService, store) {
        this.previewImageService = previewImageService;
        this.store = store;
        this.textSaved = new EventEmitter();
        this.modalOpen = false;
        this.quillFormats = [QuillToolbarFormatType.SIZE, ...BULLET_POINT_TOOLBAR_FORMATS];
        this.destroyRef = inject(DestroyRef);
        this.mapStateToComponent = (state) => {
            const report = getSelectedReport(state);
            this.reportOrientation = report.report_orientation;
            this.includeBenchmark = report.include_benchmark_default;
            this.securities = [];
            if (state.primarySecurity?.securityId) {
                this.securities.push(state.primarySecurity);
            }
            if (state.secondarySecurity?.securityId) {
                this.securities.push(state.secondarySecurity);
            }
            this.securities = [...this.securities, ...state.comparisonSecurities];
        };
    }
    ngOnInit() {
        this.store
            .select((state) => state[CUSTOM_PDF_REPORT_MODAL_STATE_SLICE_KEY])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({ next: this.mapStateToComponent });
    }
    openModal() {
        this.text = this.talkingPoint.proposal_text;
        this.loading = true;
        this.warningLoading = false;
        this.modalOpen = true;
        this.title = `Edit ${this.talkingPoint.name} Text`;
        this.previewImageService
            .getTalkingPointPreviewImage(this.securities.map((_) => _.securityId), this.talkingPoint, this.includeBenchmark)
            .subscribe({
            next: (_) => {
                this.image = _.image;
                this.loading = false;
            },
            error: () => {
                this.loading = false;
            },
        });
    }
    closeModal() {
        this.modalOpen = false;
    }
    resetDefaultText() {
        if (this.textEditComponent && this.textEditComponent.quillEditor) {
            this.textEditComponent.setText(this.defaultText);
        }
    }
    submitModal(confirm = true) {
        if (confirm) {
            this.modalOpen = false;
            if (this.newText !== this.text)
                this.textSaved.emit(this.newText);
        }
    }
    onSubmit() {
        this.warningLoading = true;
        this.previewImageService
            .getPDFReportModuleProposalsWarningMessage({
            talking_point_id: this.talkingPoint.id,
            proposal_text: this.newText,
        }, this.reportOrientation)
            .subscribe({
            next: (response) => {
                this.warningLoading = false;
                if (response.warning) {
                    this.warningModal.openModal();
                }
                else {
                    this.submitModal();
                }
            },
            error: () => {
                this.warningLoading = false;
            },
        });
    }
}
TalkingPointTextEditModalComponent.ɵfac = function TalkingPointTextEditModalComponent_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || TalkingPointTextEditModalComponent)(i0.ɵɵdirectiveInject(i1.CustomPDFReportModulePreviewImageService), i0.ɵɵdirectiveInject(i2.Store)); };
TalkingPointTextEditModalComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: TalkingPointTextEditModalComponent, selectors: [["ycn-talking-point-text-edit-modal"]], viewQuery: function TalkingPointTextEditModalComponent_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(_c0, 5);
        i0.ɵɵviewQuery(ConfirmModalComponent, 5);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.textEditComponent = _t.first);
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.warningModal = _t.first);
    } }, inputs: { talkingPoint: "talkingPoint", defaultText: "defaultText" }, outputs: { textSaved: "textSaved" }, standalone: false, decls: 10, vars: 14, consts: [["textEditComponent", ""], [3, "closeModalHandler", "open", "requiresAction"], [3, "resetModalHandler", "closeModalHandler", "header", "resetModalText"], [1, "modal-body"], [1, "modal-section"], [1, "modal-message"], [1, "loader"], [1, "mb-2", 2, "width", "100%", 3, "src"], [3, "extraFormats", "text"], [3, "cancelHandler", "submitHandler", "cancelButtonText", "submitButtonText", "loading"], [3, "confirmHandler", "confirmButtonText", "cancelButtonText", "confirmHeader", "confirmText"], [1, "loader-spinner"], [3, "textChanged", "extraFormats", "text"]], template: function TalkingPointTextEditModalComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "ycn-modal-base", 1);
        i0.ɵɵlistener("closeModalHandler", function TalkingPointTextEditModalComponent_Template_ycn_modal_base_closeModalHandler_0_listener() { return ctx.closeModal(); });
        i0.ɵɵelementStart(1, "ycn-simple-modal-header", 2);
        i0.ɵɵlistener("resetModalHandler", function TalkingPointTextEditModalComponent_Template_ycn_simple_modal_header_resetModalHandler_1_listener() { return ctx.resetDefaultText(); })("closeModalHandler", function TalkingPointTextEditModalComponent_Template_ycn_simple_modal_header_closeModalHandler_1_listener() { return ctx.closeModal(); });
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(2, "div", 3)(3, "div", 4)(4, "div", 5);
        i0.ɵɵconditionalCreate(5, TalkingPointTextEditModalComponent_Conditional_5_Template, 2, 0, "div", 6);
        i0.ɵɵconditionalCreate(6, TalkingPointTextEditModalComponent_Conditional_6_Template, 1, 1, "img", 7);
        i0.ɵɵconditionalCreate(7, TalkingPointTextEditModalComponent_Conditional_7_Template, 2, 2, "ycn-text-edit", 8);
        i0.ɵɵelementEnd()()();
        i0.ɵɵelementStart(8, "ycn-simple-modal-footer", 9);
        i0.ɵɵlistener("cancelHandler", function TalkingPointTextEditModalComponent_Template_ycn_simple_modal_footer_cancelHandler_8_listener() { return ctx.closeModal(); })("submitHandler", function TalkingPointTextEditModalComponent_Template_ycn_simple_modal_footer_submitHandler_8_listener() { return ctx.onSubmit(); });
        i0.ɵɵelementEnd()();
        i0.ɵɵelementStart(9, "ycn-confirm-modal", 10);
        i0.ɵɵlistener("confirmHandler", function TalkingPointTextEditModalComponent_Template_ycn_confirm_modal_confirmHandler_9_listener($event) { return ctx.submitModal($event); });
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵproperty("open", ctx.modalOpen)("requiresAction", true);
        i0.ɵɵadvance();
        i0.ɵɵproperty("header", ctx.title)("resetModalText", "Reset Default Text");
        i0.ɵɵadvance(4);
        i0.ɵɵconditional(ctx.loading ? 5 : -1);
        i0.ɵɵadvance();
        i0.ɵɵconditional(!ctx.loading && ctx.image ? 6 : -1);
        i0.ɵɵadvance();
        i0.ɵɵconditional(ctx.modalOpen ? 7 : -1);
        i0.ɵɵadvance();
        i0.ɵɵproperty("cancelButtonText", "Cancel")("submitButtonText", "Update")("loading", ctx.warningLoading);
        i0.ɵɵadvance();
        i0.ɵɵproperty("confirmButtonText", "Continue")("cancelButtonText", "Cancel")("confirmHeader", "Alert")("confirmText", "The text entered may be too long and flow into the module below. Continue or update with a shortened version.");
    } }, dependencies: [i3.ConfirmModalComponent, i4.ModalBaseComponent, i5.SimpleModalFooterComponent, i6.SimpleModalHeaderComponent, i7.TextEditComponent], encapsulation: 2 });
